import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { LinkItem } from '../../models/common';
import {
  AuthenticationService,
  IRequestOTP,
  OtpAuthRequest,
  PasscodeAuthRequest,
  PasscodeChallenge,
  PasscodeChallengeContainer,
  PasscodeOtpAuthRequest,
  SendOtpResponse,
  SignInOtpResponse,
  SignInResponse,
  SignInUnboundResponse,
} from '../../services/authentication.service';
import { PasscodeComponent } from '../passcode/passcode.component';
import { OtpInputComponent } from '../form/strong-customer-authentication/otp-input/otp-input.component';
import { BrandService } from '../../services/brand.service';
import { AnalyticsService } from '../../services/analytics.service';
import { NativeBridge } from '../../services/nativeBridge';
import { ErrorService } from '../../services/error.service';
import { BrandedRouter } from '../../services/brandedRouter';
import { InstrumentationService } from '../../services/instrumentation.service';
import { UsernameComponent } from './username/username.component';
import { WindowRef } from '../../services/windowRef';
import { ContentService } from '../../services/content.service';
import { ThreatMetrixService } from '../../services/threat-metrix.service';
import {
  AuthenticationLevel,
  IOTPRequestBuilder,
  OtpChannel,
} from '../../services/strong-customer-authentication/model';
import { CredentialsMessage } from './CredentialsMessage';
import { LoginService } from '../../services/login.service';
import {
  LoggedOutEntitlement,
  LoggedOutEntitlementsService,
} from '../../services/logged-out-entitlements.service';
import { AppType } from '../../services/compatibility.service';
import { LivePersonService } from '../../services/live-person.service';
import {
  LivePersonErrors,
  livePersonErrorDictionary,
} from '../../enums/liveperson-errors';
import { ClientVariableService } from '../../services/clientConfigVariable.service';
import {
  gtmEventTracking,
  GTMTracking,
} from '../../enums/google-analytics-tracking';

export enum LoginJourney {
  Username = 'Username',
  Bound = 'Bound',
  Unbound = 'Unbound',
  OtpDeliverySelection = 'OtpDeliverySelection',
  OtpByPhone = 'OtpByPhone',
  OtpInput = 'OtpInput',
  UnboundInvalidCredentials = 'UnboundInvalidCredentials',
  UnableToSendOtp = 'UnableToSendOtp',
}

@Component({
  selector: '[data-component=login-sca]',
  templateUrl: './login-sca.component.html',
})
export class LoginSCAComponent implements OnInit, OnDestroy {
  @Input() loginReady: boolean;
  LoginJourney: typeof LoginJourney = LoginJourney;
  @ViewChild('usernameComponent') usernameComponent: UsernameComponent;
  otpChannel: typeof OtpChannel = OtpChannel;
  @ViewChild('passcodeComponent') passcodeComponent: PasscodeComponent;
  @ViewChild('otpInputComponent') otpInputComponent: OtpInputComponent;
  private returnUrl: string = 'account-summary?tailored=true';
  private challengeVerificationToken: string;
  private sessionData: string;
  private tmxSessionId: string;
  private userName: string;
  public alertMessage: string = null;
  public unauthenticatedChatMessage: string = '';
  public resendErrorMessage: boolean;
  public errors: Array<any> = [];
  public errorMessages: any = {
    username: 'Username is required.',
    passcode: 'Passcode is required.',
    otp: 'Otp is required.',
  };
  public credentialsMessage: CredentialsMessage = {
    url: '',
    message: '',
    srMessage: '',
    detail: '',
    linkText: '',
  };
  public GTMTracking = GTMTracking;
  public passcodeInputs: any[] = [];
  public otpInputHidden: boolean = false;
  public loginForm: FormGroup;
  public step: string = LoginJourney[LoginJourney.Username];
  public forgotUsernameLink: LinkItem;
  public forgotPasscodeLink: LinkItem;
  public registerLink: LinkItem;
  public formDirty: boolean = false;
  public isSubmitting: boolean = false;
  public isResendingOTP: boolean = false;
  public resendSuccessful: boolean;
  public resendMessage: string;
  public screenreaderErrorMessages: string = '';
  public mobileLinksEnabled: boolean;
  public brand: string;
  public googlePlayLink: string;
  public appStoreLink: string;
  public mobileAppName: string;
  public AppType = AppType;

  public contactUsLink: string;
  public mobileNumberEnding: string;
  public homeNumberEnding: string;
  public contactUsUrl: string;
  public notMyNumberDropdownOpen: boolean = false;
  public backToLoginLink: string;
  public loading: boolean = true;
  private otpOptions: string;
  public loginButtonSubmitted: boolean = false;
  public psd2LoginBannerEnabled = false;
  public loginVimeoLinkEnabled: boolean = false;
  public separatePasscodeValidation: boolean = false;
  public showHelpPopup: boolean = false;
  public isOwnBrand: boolean = false;
  public otpInputLabel: string = 'Please enter your 6 digit OTP ';
  public showLoginError: boolean = false;
  public buildRoot = this.clientVariableService.getBuildRoot();
  private timeoutId: number;
  private attemptsRemaining: number;

  constructor(
    private activatedRoute: ActivatedRoute,
    //TODO: Must be converted to private and tests updated
    public authenticationService: AuthenticationService,
    private brandedRouter: BrandedRouter,
    private brandService: BrandService, // todo using brand service and brand router
    private elementRef: ElementRef,
    public errorService: ErrorService,
    private nativeBridge: NativeBridge,
    private analyticsService: AnalyticsService,
    private windowRef: WindowRef,
    private contentService: ContentService,
    private threatMetrixService: ThreatMetrixService,
    private loginService: LoginService,
    private loggedOutEntitlementsService: LoggedOutEntitlementsService,
    private livePersonService: LivePersonService,
    private clientVariableService: ClientVariableService,
    instrumentation: InstrumentationService
  ) {
    this.brand = this.brandService.getBrand();
    this.isOwnBrand = this.brandService.isOwnBrand(this.brand);
    this.checkEntitlements();
    this.loadContent();
    this.contactUsLink = brandedRouter.getBrandedLink('help/contact-us');

    instrumentation.mark('LoginComponent constructor start');

    this.errorService.clear();
    this.initLinks();

    instrumentation.mark('LoginComponent constructor end');
    instrumentation.measure(
      'LoginComponent constructor',
      'LoginComponent constructor start',
      'LoginComponent constructor end'
    );
  }

  ngOnInit() {
    // If a user clicks login on recover username, or ends up at login by some other mechanism
    // let the native app know so it can remove the webview
    this.nativeBridge.notifyTaskCompleted();
    this.analyticsService.trackPageView(
      `${this.brand}/${gtmEventTracking[GTMTracking.LoginVisitPage].url}`,
      gtmEventTracking[GTMTracking.LoginVisitPage].title
    );
    if (this.step === LoginJourney[LoginJourney.Username]) {
      this.gotoStepUsername();
    }

    this.loginReady = true;
    this.activatedRoute.queryParams.subscribe((params: Params) => {
      if (
        typeof params['ReturnUrl'] !== 'undefined' &&
        params['ReturnUrl'].length > 0
      )
        this.returnUrl = params['ReturnUrl'];
    });

    this.initForm();
    this.contactUsUrl = this.brandedRouter.getBrandedLink('help/contact-us');
  }

  ngOnDestroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  trackCtaActions(event: GTMTracking) {
    this.analyticsService.trackCtaActions(event);
  }

  public fieldHasError(name: string) {
    const control = this.loginForm.controls[name];
    return control.invalid && (control.dirty || control.touched);
  }

  public fieldHasRequiredError(name: string) {
    const control = this.loginForm.controls[name];
    return (
      control.invalid &&
      (control.dirty || control.touched) &&
      control.errors.required
    );
  }

  public getTextForSubmitButton(): string {
    switch (this.step) {
      case LoginJourney[LoginJourney.Username]:
        return 'Next';
      case LoginJourney[LoginJourney.Bound]:
      case LoginJourney[LoginJourney.OtpInput]:
        return 'Log in';
      case LoginJourney[LoginJourney.Unbound]:
        return 'Continue';
      case LoginJourney[LoginJourney.OtpDeliverySelection]:
        return this.getSelectedOtpMethod() === OtpChannel.Text
          ? 'Send OTP'
          : 'Call Me';
      case LoginJourney[LoginJourney.UnableToSendOtp]:
        return 'Back';
    }
  }

  public submitLogin() {
    this.formDirty = true;
    switch (this.step) {
      case LoginJourney[LoginJourney.Username]:
        this.submitUsername();
        break;
      case LoginJourney[LoginJourney.Bound]:
        this.submitPasscodeAndBinding();
        break;
      case LoginJourney[LoginJourney.Unbound]:
        this.handleSubmitPasscodeUnbound();
        break;
      case LoginJourney[LoginJourney.OtpDeliverySelection]:
        this.resendErrorMessage = false;
        this.submitOtpDeliverySelection();
        break;
      case LoginJourney[LoginJourney.OtpInput]:
        this.handleSubmitOtp();
        break;
      case LoginJourney[LoginJourney.UnboundInvalidCredentials]:
        this.goToSubmitPasscodeAndBinding();
        break;
      case LoginJourney[LoginJourney.UnableToSendOtp]:
        this.goToStepUnableToSendOtp();
        break;
    }
  }

  public onIsSubmittingChange(submitting: boolean) {
    this.isSubmitting = submitting;
  }

  public onUsernameResponseChange(result: PasscodeChallengeContainer) {
    if (result.isAccountLocked) {
      this.brandedRouter.navigate('/error/account-locked');
      return;
    }

    this.challengeVerificationToken = result.challengeVerificationToken;
    this.passcodeInputs = this.getPasscodeArray(result.challenge);
    this.sessionData = result.sessionData;
    this.mobileNumberEnding = result.mobileNumberEnding;
    this.homeNumberEnding = result.homeNumberEnding;

    if (result.authLevel === AuthenticationLevel.PartialBound) {
      this.gotoStepBound();
      return;
    }

    if (!result.homeNumberEnding && !result.mobileNumberEnding) {
      this.gotoStepUnableToSendOtp();
      return;
    }

    this.gotoStepUnbound();
  }

  public onUserNameChange(username: string) {
    this.userName = username;
  }

  public async resendOTPClickHandler() {
    this.trackCtaActions(GTMTracking.LoginClickResentAgain);
    const request = this.buildOTPRequest({
      isResend: true,
    });

    this.resendSuccessful = false;
    this.isResendingOTP = true;
    await this.sendOTPRequest(request);
    this.isResendingOTP = false;
  }

  private async submitOtpDeliverySelection(): Promise<void> {
    const request = this.buildOTPRequest({
      isResend: false,
    });

    this.isSubmitting = true;
    await this.sendOTPRequest(request);
    this.isSubmitting = false;
  }

  public async sendOTPRequest(dto: IRequestOTP): Promise<void> {
    this.clearErrors();

    try {
      const res = await this.authenticationService.requestOTP(dto);
      this.sessionData = res.sessionData;
      this.handleOTPSubmissionResponse(dto, res);
    } catch (err) {
      this.errorService.processError(err);
    }
  }

  public onNotMyUsername(e: Event) {
    e.preventDefault();
    this.trackCtaActions(GTMTracking.LoginClickNotYou);
    this.initForm();
    this.gotoStepUsername();
  }

  public showPopup(e: Event) {
    e.preventDefault();
    this.showHelpPopup = true;
  }

  public hidePopup() {
    this.showHelpPopup = false;
  }

  private buildOTPRequest(args: IOTPRequestBuilder): IRequestOTP {
    const request: IRequestOTP = {
      userName: this.userName,
      sessionData: this.sessionData,
      channel: this.getSelectedOtpMethod(),
      isResend: args.isResend,
    };

    return request;
  }

  private handleOTPSubmissionResponse(
    request: IRequestOTP,
    response: SendOtpResponse
  ): void {
    if (response.canResend) {
      if (request.channel === OtpChannel.Call) {
        this.goToCallWaitingPage(request.isResend);
      } else {
        this.handleStepChange(LoginJourney.OtpInput);
        this.analyticsService.trackPageView(
          `${this.brand}/${
            gtmEventTracking[GTMTracking.LoginVisitPageOTPEntry].url
          }`,
          gtmEventTracking[GTMTracking.LoginVisitPageOTPEntry].title
        );
        if (request.isResend) {
          this.resendSuccessful = true;
          this.resendMessage = 'Your OTP has been resent';
        }
      }
    } else {
      this.resendErrorMessage = true;
    }
  }

  private clearErrors() {
    this.errorService.clear();
    this.errors = [];
  }

  private handleSubmitPasscodeUnbound(): void {
    this.passcodeComponent.submit();
    if (this.loginForm.controls['passcode'].invalid) return;

    if (this.separatePasscodeValidation) {
      this.submitPasscodeUnbound();
    } else {
      this.loadOtpPreference();
      this.handleStepChange(LoginJourney.OtpDeliverySelection);
      this.analyticsService.trackPageView(
        `${this.brand}/${gtmEventTracking[GTMTracking.LoginVisitPageOTP].url}`,
        gtmEventTracking[GTMTracking.LoginVisitPageOTP].title
      );
      this.windowRef.setFocusById('authcodeByText');
    }
  }

  private handleSubmitOtp(): void {
    if (this.separatePasscodeValidation) {
      this.submitOtp();
    } else {
      this.submitPasscodeAndOtp();
    }
  }

  private goToSubmitPasscodeAndBinding(): void {
    this.gotoStepUnbound();
  }

  private goToStepUnableToSendOtp(): void {
    this.gotoStepUsername();
  }

  private goToCallWaitingPage(isResend: boolean): void {
    this.handleStepChange(LoginJourney.OtpByPhone);
    this.timeoutId = setTimeout(
      (() => {
        this.handleStepChange(LoginJourney.OtpInput);
        this.analyticsService.trackPageView(
          `${this.brand}/${
            gtmEventTracking[GTMTracking.LoginVisitPageOTPEntry].url
          }`,
          gtmEventTracking[GTMTracking.LoginVisitPageOTPEntry].title
        );
        if (isResend) {
          this.resendSuccessful = true;
          this.resendMessage = "We've just called you and provided a new code";
        }
      }) as TimerHandler,
      10000
    );
  }

  private async checkEntitlements() {
    this.mobileLinksEnabled =
      await this.loggedOutEntitlementsService.hasEntitlement(
        LoggedOutEntitlement.mobileAppBanner
      );
    this.psd2LoginBannerEnabled =
      await this.loggedOutEntitlementsService.hasEntitlement(
        LoggedOutEntitlement.psd2LoginBanner
      );
    this.separatePasscodeValidation =
      await this.loggedOutEntitlementsService.hasEntitlement(
        LoggedOutEntitlement.scaLoginSeparatePasscodeValidation
      );
    this.loginVimeoLinkEnabled =
      await this.loggedOutEntitlementsService.hasEntitlement(
        LoggedOutEntitlement.loginVimeoLink
      );
  }

  private loadContent(): void {
    this.contentService.getContent().subscribe((result) => {
      if (result === null) {
        return;
      }

      this.googlePlayLink = result.global.googlePlayStoreAppLink;
      this.appStoreLink = result.global.itunesStoreAppLink;
      this.mobileAppName =
        result.apps && result.apps.name ? result.apps.name : this.brand;
    });
  }

  private initForm() {
    this.loginForm = new FormGroup({
      usernameGroup: new FormGroup({
        userName: new FormControl([Validators.required]),
        rememberMe: new FormControl(false),
      }),
      passcode: new FormControl(),
      authCodePreference: new FormControl('', [Validators.required]),
      otp: new FormControl('', [Validators.required]),
    });
  }

  private initLinks() {
    this.forgotUsernameLink = new LinkItem(
      'Forgotten username?',
      this.brandedRouter.getBrandedLink('/recovery/retrieve-username'),
      false,
      'btnRetrieveUsername'
    );
    this.forgotPasscodeLink = new LinkItem(
      'Forgotten passcode?',
      this.brandedRouter.getBrandedLink('/reset-passcode'),
      false,
      'btnResetPasscode'
    );

    this.registerLink = new LinkItem(
      'Sign up here',
      this.brandedRouter.getBrandedLink('/register'),
      false
    );
  }

  private gotoStepUsername() {
    this.analyticsService.trackPageView(
      `${this.brand}/login/step1`,
      'Login Step One'
    );
    this.gotoStep(LoginJourney[LoginJourney.Username]);
  }

  private gotoStepBound() {
    this.analyticsService.trackPageView(
      `${this.brand}/login/step2`,
      'Login Step Two'
    );
    this.gotoStep(LoginJourney[LoginJourney.Bound]);
  }

  private gotoStepUnbound() {
    this.analyticsService.trackPageView(
      `${this.brand}/login/step3`,
      'Login Step Three'
    );
    const loginGAObject = gtmEventTracking[GTMTracking.LoginVisitPagePasscode];
    this.analyticsService.trackPageView(
      `${this.brand}/${loginGAObject.url}`,
      loginGAObject.title
    );
    this.gotoStep(LoginJourney[LoginJourney.Unbound]);
  }

  private gotoStepUnableToSendOtp() {
    this.analyticsService.trackPageView(
      `${this.brand}/login/step4`,
      'Login Step Four'
    );
    this.gotoStep(LoginJourney[LoginJourney.UnableToSendOtp]);
  }

  private handleStepChange(step: LoginJourney) {
    this.analyticsService.trackPageView(
      `${this.brand}/login/step${step}`,
      `Login Step ${step}`
    );
    this.gotoStep(step);
  }

  private gotoStep(step: string): void {
    this.credentialsMessage = {
      url: '',
      message: '',
      srMessage: '',
      detail: '',
      linkText: '',
    };
    this.formDirty = false;
    this.step = step;
    this.scrollToTop();
    this.setPasscodeFocus(50);
  }

  private scrollToTop() {
    const yPos = this.elementRef.nativeElement.offsetTop;
    this.windowRef.scrollTo(0, yPos);
  }

  private setPasscodeFocus(delay: number) {
    this.timeoutId = setTimeout(
      (() => {
        if (this.passcodeComponent == null) return;

        this.passcodeComponent.setFocus();
      }) as TimerHandler,
      delay
    );
  }

  private submitUsername() {
    this.usernameComponent.submit();
  }

  private getPasscodeArray(challenge: PasscodeChallenge): Array<boolean> {
    const arr = [];
    if (challenge.passcodeLength == null || challenge.passcodeLength == 0) {
      challenge.passcodeLength = 6;
    }

    // add blanks
    for (let i = 0; i < challenge.passcodeLength; i++) {
      arr.push(false);
    }

    // add inputs
    challenge.passcodeDigits.forEach((digit) => {
      arr[digit.position] = true;
    });

    return arr;
  }

  private getUnauthenticatedChatMessage(errorCode, errorMsg) {
    if (this.attemptsRemaining > 0 && this.attemptsRemaining <= 3) {
      this.livePersonService.sendLivePersonErrorTracking(errorCode, errorMsg);
      this.showLoginError = true;
    } else {
      this.showLoginError = false;
    }
  }

  private submitPasscodeAndBinding() {
    this.passcodeComponent.submit();

    if (this.loginForm.controls['passcode'].invalid) return;

    this.isSubmitting = true;

    const request: PasscodeAuthRequest = {
      username: this.userName,
      passcodeDigits: this.loginForm.controls['passcode'].value,
      challengeVerificationToken: this.challengeVerificationToken,
      tmxSessionId: this.threatMetrixService.getSessionId('Login'),
    };

    this.errorService.clear();
    this.errors = [];

    this.authenticationService.submitPasscodeBindingChallenge(request).then(
      (response: SignInResponse) => {
        const { attemptsRemaining, success } = response;
        this.attemptsRemaining = attemptsRemaining;

        // positive promise resolving doesn't mean that binding challenge was succesfull, we have to check 'success' flag for that
        if (success) {
          // Clears the LP tracking so the messaging button does not display
          this.livePersonService.clearLivePersonErrorTracking();
          this.livePersonService.closeChatWindow();
          this.loginService.loginRedirect(this.returnUrl);
        } else {
          const code = LivePersonErrors.PasscodeError;
          const { msg } = livePersonErrorDictionary[code];
          this.getUnauthenticatedChatMessage(code, msg);
          this.passcodeComponent.reset();
          this.isSubmitting = false;
          const attempts =
            this.attemptsRemaining === 1 ? 'attempt' : 'attempts';
          this.alertMessage = `You have entered invalid details. You have <strong>${this.attemptsRemaining} login ${attempts} left.</strong>`;
        }
      },
      (error) => {
        this.passcodeComponent.reset();
        this.isSubmitting = false;
        this.alertMessage = null;
        this.errorService.processError(error);
      }
    );
  }

  private async submitPasscodeUnbound() {
    this.passcodeComponent.submit();

    if (this.loginForm.controls['passcode'].invalid) return;

    this.isSubmitting = true;

    const request: PasscodeAuthRequest = {
      username: this.userName,
      passcodeDigits: this.loginForm.controls['passcode'].value,
      challengeVerificationToken: this.challengeVerificationToken,
      tmxSessionId: this.threatMetrixService.getSessionId('Login'),
    };

    this.errorService.clear();
    this.errors = [];
    this.alertMessage = null;

    try {
      const response: SignInUnboundResponse =
        await this.authenticationService.submitPasscodeUnboundChallenge(
          request
        );
      const {
        attemptsRemaining,
        success,
        isAccountLocked,
        challengeVerificationToken,
      } = response;
      this.attemptsRemaining = attemptsRemaining;

      if (isAccountLocked) {
        await this.brandedRouter.navigate('/error/account-locked');
        return;
      }
      if (success) {
        // Clears the LP tracking so the messaging button does not display
        this.livePersonService.clearLivePersonErrorTracking();
        this.loadOtpPreference();
        this.handleStepChange(LoginJourney.OtpDeliverySelection);
        this.analyticsService.trackPageView(
          `${this.brand}/${
            gtmEventTracking[GTMTracking.LoginVisitPageOTP].url
          }`,
          gtmEventTracking[GTMTracking.LoginVisitPageOTP].title
        );
        this.challengeVerificationToken = challengeVerificationToken;
        this.isSubmitting = false;
        this.windowRef.setFocusById('authcodeByText');
      } else {
        const code = LivePersonErrors.PasscodeError;
        const { msg } = livePersonErrorDictionary[code];
        this.getUnauthenticatedChatMessage(code, msg);
        this.passcodeComponent.reset();
        this.isSubmitting = false;
        const attempts = this.attemptsRemaining === 1 ? 'attempt' : 'attempts';
        this.alertMessage = `<p>You have entered incorrect login details. Please review the details you have provided.</p><p>You have <strong>${this.attemptsRemaining} login ${attempts} left.</strong></p>`;
        this.analyticsService.pushToDataLayer(
          gtmEventTracking[GTMTracking.LoginPasscodeError]
        );
      }
    } catch (error) {
      this.passcodeComponent.reset();
      this.isSubmitting = false;
      this.alertMessage = null;
      await this.errorService.processError(error);
    }
  }

  private async submitOtp() {
    this.loginButtonSubmitted = true;

    if (!this.otpInputComponent.isValid()) return;

    this.isSubmitting = true;

    const request: OtpAuthRequest = {
      username: this.userName,
      challengeVerificationToken: this.challengeVerificationToken,
      tmxSessionId: this.tmxSessionId,
      sessionData: this.sessionData,
      otp: this.otpInputComponent.submit(),
      channel:
        this.getSelectedOtpMethod() === OtpChannel.Text
          ? OtpChannel.Text
          : OtpChannel.Call,
    };

    this.errorService.clear();
    this.errors = [];
    this.alertMessage = null;

    try {
      const response: SignInOtpResponse =
        await this.authenticationService.submitOtpChallenge(request);
      const { attemptsRemaining, success, isAccountLocked } = response;
      this.attemptsRemaining = attemptsRemaining;

      if (isAccountLocked) {
        await this.brandedRouter.navigate('/error/account-locked');
        return;
      }
      if (success) {
        // Clears the LP tracking so the messaging button does not display
        this.livePersonService.clearLivePersonErrorTracking();
        this.livePersonService.closeChatWindow();
        await this.loginService.loginRedirect(this.returnUrl);
      } else {
        this.loginButtonSubmitted = false;
        this.isSubmitting = false;
        if (!success) {
          const code = LivePersonErrors.OTPError;
          const { msg } = livePersonErrorDictionary[code];
          this.getUnauthenticatedChatMessage(code, msg);
          const attempts =
            this.attemptsRemaining === 1 ? 'attempt' : 'attempts';
          this.alertMessage = `<p>You have entered incorrect login details. Please review the details you have provided.</p><p>You have <strong>${this.attemptsRemaining} login ${attempts} left.</strong></p>`;
          this.analyticsService.pushToDataLayer(
            gtmEventTracking[GTMTracking.LoginOTPError]
          );
        }
      }
    } catch (error) {
      this.otpInputComponent.reset();
      this.loginButtonSubmitted = false;
      this.isSubmitting = false;
      this.alertMessage = null;
      await this.errorService.processError(error);
    }
  }

  private submitPasscodeAndOtp() {
    this.loginButtonSubmitted = true;

    if (!this.otpInputComponent.isValid()) return;

    this.isSubmitting = true;

    const request: PasscodeOtpAuthRequest = {
      username: this.userName,
      passcodeDigits: this.loginForm.controls['passcode'].value,
      challengeVerificationToken: this.challengeVerificationToken,
      tmxSessionId: this.tmxSessionId,
      sessionData: this.sessionData,
      otp: this.otpInputComponent.submit(),
      channel:
        this.getSelectedOtpMethod() === OtpChannel.Text
          ? OtpChannel.Text
          : OtpChannel.Call,
    };

    this.errorService.clear();
    this.errors = [];

    this.authenticationService.submitPasscodeOtpChallenge(request).then(
      (response: SignInOtpResponse) => {
        if (response.isAccountLocked) {
          this.brandedRouter.navigate('/error/account-locked');
          return;
        }
        // positive promise resolving doesn't mean that binding challenge was succesfull, we have to check 'success' flag for that
        if (response.success) {
          // Clears the LP tracking so the messaging button does not display
          this.livePersonService.clearLivePersonErrorTracking();
          this.livePersonService.closeChatWindow();
          this.loginService.loginRedirect(this.returnUrl);
        } else {
          this.otpInputComponent.reset();
          this.loginButtonSubmitted = false;
          this.isSubmitting = false;
          if (!response.success) {
            const code = LivePersonErrors.PasscodeOTPError;
            const { msg } = livePersonErrorDictionary[code];
            this.getUnauthenticatedChatMessage(code, msg);
            const attempts =
              response.attemptsRemaining === 1 ? 'attempt' : 'attempts';
            this.alertMessage = `You have ${response.attemptsRemaining} login ${attempts} remaining.`;
            this.handleStepChange(LoginJourney.UnboundInvalidCredentials);
          }
        }
      },
      (error) => {
        this.otpInputComponent.reset();
        this.loginButtonSubmitted = false;
        this.isSubmitting = false;
        this.alertMessage = null;
        this.errorService.processError(error);
      }
    );
  }

  private loadOtpPreference(): void {
    if (this.mobileNumberEnding) {
      this.otpOptions = OtpChannel[OtpChannel.Text];
    } else {
      this.otpOptions = OtpChannel[OtpChannel.Call];
    }
    this.setFormValues();
  }

  private setFormValues(): void {
    const selected =
      this.otpOptions === OtpChannel[OtpChannel.Text]
        ? OtpChannel.Text
        : OtpChannel.Call;
    this.loginForm.controls['authCodePreference'].setValue(selected);
  }

  public getSelectedOtpMethod(): OtpChannel {
    return this.loginForm.controls['authCodePreference'].value;
  }

  public doNotDisplaySplitColumns(): boolean {
    return (
      this.step !== LoginJourney[LoginJourney.UnableToSendOtp] &&
      this.step !== LoginJourney[LoginJourney.UnboundInvalidCredentials] &&
      this.step !== LoginJourney[LoginJourney.OtpByPhone]
    );
  }

  public submitButtonAriaDescribedBy(): string {
    return this.fieldHasError('userName') ||
      (this.step === LoginJourney[LoginJourney.Bound] &&
        this.passcodeComponent &&
        !this.passcodeComponent.valid) ||
      this.credentialsMessage.srMessage
      ? 'sr-errors'
      : null;
  }

  public isFormSubmitting(): boolean {
    return this.isSubmitting;
  }
}
